import type { TFunction } from "i18next";
import { STRENGTH_DOMAIN_MAP } from "@/utils/constants/strengths";

const DOMAIN_I18N_KEYS: Record<string, string> = {
  "strategic thinking": "strategicThinking",
  strategic: "strategicThinking",
  executing: "executing",
  influencing: "influencing",
  "relationship building": "relationshipBuilding",
  relationship: "relationshipBuilding",
};

const normalizeLookup = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

const isArabic = (language: string) => language?.split("-")[0] === "ar";

export const getStrengthI18nKey = (strengthName: string) => {
  const words = strengthName
    .trim()
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);

  if (words.length === 0) return "";

  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join("");
};

export const translateStrengthName = (
  strengthName: string | undefined,
  t: TFunction,
  language: string
) => {
  if (!strengthName) return "";
  if (!isArabic(language)) return strengthName;

  const key = getStrengthI18nKey(strengthName);
  return key ? t(`strengths.${key}`, { defaultValue: strengthName }) : strengthName;
};

export const translateDomainName = (
  domainName: string | undefined,
  t: TFunction,
  language: string
) => {
  if (!domainName) return "";
  if (!isArabic(language)) return domainName;

  const key = DOMAIN_I18N_KEYS[normalizeLookup(domainName)];
  return key ? t(`domains.${key}`, { defaultValue: domainName }) : domainName;
};

export const getStrengthDescription = (
  strengthName: string | undefined,
  domainName: string | undefined,
  description: string | undefined,
  t: TFunction,
  language: string
) => {
  if (!isArabic(language)) {
    return description || (strengthName && domainName ? `${strengthName} strength in the ${domainName} domain.` : "");
  }

  const localizedStrength = translateStrengthName(strengthName, t, language);
  const localizedDomain = translateDomainName(domainName, t, language);

  if (!localizedStrength || !localizedDomain) {
    return description || localizedStrength || "";
  }

  return `موهبة ${localizedStrength} ضمن مجال ${localizedDomain}.`;
};

export const getDomainForStrengthName = (strengthName: string | undefined) => {
  if (!strengthName) return undefined;

  const normalized = normalizeLookup(strengthName);
  const match = Object.entries(STRENGTH_DOMAIN_MAP).find(
    ([name]) => normalizeLookup(name) === normalized
  );

  return match?.[1];
};
